<template>
  <div class="usage-list">
    <div class="page-header">
      <h2>原料使用</h2>
      <a-space>
        <a-button @click="handleExport" :loading="exportLoading">
          <template #icon><DownloadOutlined /></template>
          导出Excel
        </a-button>
        <a-button type="primary" @click="openEdit(null)">
          <template #icon><PlusOutlined /></template>
          新增使用
        </a-button>
      </a-space>
    </div>

    <a-card class="filter-card">
      <a-form layout="inline">
        <a-form-item label="原料">
          <a-select
            v-model:value="searchForm.materialId"
            placeholder="请选择原料"
            allow-clear
            show-search
            :filter-option="filterOption"
            style="width: 220px"
          >
            <a-select-option v-for="m in materials" :key="m.id" :value="m.id" :label="m.name">
              {{ m.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="类型">
          <a-select v-model:value="searchForm.type" placeholder="全部" allow-clear style="width: 120px">
            <a-select-option value="FABRIC">面料</a-select-option>
            <a-select-option value="ACCESSORY">辅料</a-select-option>
            <a-select-option value="CONSUMABLE">耗材</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="日期">
          <a-range-picker v-model:value="searchForm.dateRange" format="YYYY-MM-DD" style="width: 240px" />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" @click="handleSearch">查询</a-button>
            <a-button @click="handleReset">重置</a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <a-card>
      <a-table
        :columns="columns"
        :data-source="records"
        :loading="loading"
        :pagination="pagination"
        @change="handleTableChange"
        row-key="id"
        :scroll="{ x: 1100 }"
        :sticky="{ offsetScroll: 0, getContainer: getScrollContainer }"
        size="middle"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'usageDate'">{{ formatDate(record.usageDate) }}</template>
          <template v-if="column.key === 'type'">
            <a-tag :color="typeColor(record.type)">{{ typeLabel(record.type) }}</a-tag>
          </template>
          <template v-if="column.key === 'quantity'">
            <strong>{{ formatNum(record.quantity) }}</strong> {{ unitText(record.unit) }}
          </template>
          <template v-if="column.key === 'action'">
            <a-space :size="4">
              <a-button type="link" size="small" @click="openEdit(record)">编辑</a-button>
              <a-popconfirm title="确定删除该使用记录吗？" @confirm="handleDelete(record)">
                <a-button type="link" size="small" danger>删除</a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-modal
      v-model:open="editVisible"
      :title="form.id ? '编辑使用' : '新增使用'"
      @ok="handleSubmit"
      :confirm-loading="submitting"
      width="600px"
    >
      <a-form layout="vertical">
        <a-row :gutter="16">
          <a-col :span="14">
            <a-form-item label="供应商筛选">
              <a-auto-complete
                v-model:value="formSupplierFilter"
                :options="supplierOptions"
                :filter-option="supplierFilter"
                placeholder="选择供应商后，下方原料按该供应商排序（不会保存）"
                style="width: 100%"
                allow-clear
              />
            </a-form-item>
          </a-col>
          <a-col :span="10">
            <a-form-item label="使用日期">
              <a-date-picker v-model:value="form.usageDate" format="YYYY-MM-DD" style="width: 100%" />
            </a-form-item>
          </a-col>
          <a-col :span="14">
            <a-form-item label="原料" required>
              <a-radio-group v-model:value="formTypeFilter" size="small" button-style="solid" style="margin-bottom: 6px">
                <a-radio-button value="">全部</a-radio-button>
                <a-radio-button value="FABRIC">面料</a-radio-button>
                <a-radio-button value="ACCESSORY">辅料</a-radio-button>
                <a-radio-button value="CONSUMABLE">耗材</a-radio-button>
              </a-radio-group>
              <a-select
                v-model:value="form.materialId"
                placeholder="请选择原料（可搜索）"
                show-search
                :filter-option="filterOption"
              >
                <a-select-option v-for="m in filteredFormMaterials" :key="m.id" :value="m.id" :label="m.name">
                  <span v-if="matchesSupplier(m)" style="color:#1890ff;font-weight:500;margin-right:4px">●</span>
                  {{ m.name }} <span style="color:#999">({{ typeLabel(m.type) }} / {{ unitText(m.unit) }})</span>
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          <a-col v-if="currentMaterial" :span="24">
            <div class="stock-info">
              <span class="stock-info-label">当前库存</span>
              <span :class="['stock-info-value', stockClass]">
                {{ formatNum(currentMaterial.stock) }} {{ unit }}
              </span>
              <span class="stock-info-detail">
                累计采购 {{ formatNum(currentMaterial.totalPurchased) }} {{ unit }} ·
                累计使用 {{ formatNum(currentMaterial.totalUsed) }} {{ unit }}
              </span>
              <span v-if="exceedsStock" class="stock-info-warn">
                ⚠️ 本次使用超过库存余量
              </span>
            </div>
          </a-col>
          <a-col :span="10">
            <a-form-item :label="`数量（${unit}）`" required>
              <a-input-number v-model:value="form.quantity" :min="0" :precision="2" placeholder="0.00" style="width: 100%" />
            </a-form-item>
          </a-col>
          <a-col :span="14">
            <a-form-item label="用途">
              <a-input v-model:value="form.purpose" placeholder="如：裁某批次（可选）" :maxlength="500" />
            </a-form-item>
          </a-col>
          <a-col :span="24">
            <a-form-item label="备注">
              <a-textarea v-model:value="form.remark" :rows="2" :maxlength="500" show-count />
            </a-form-item>
          </a-col>
        </a-row>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { ref, shallowRef, reactive, computed, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import { PlusOutlined, DownloadOutlined } from '@ant-design/icons-vue'
import dayjs from 'dayjs'
import { materialApi } from '@/api'

export default {
  name: 'UsageList',
  components: { PlusOutlined, DownloadOutlined },
  setup() {
    const loading = ref(false)
    const submitting = ref(false)
    const exportLoading = ref(false)
    const records = shallowRef([])  // 纯展示大列表：shallowRef 免深度响应式
    const materials = ref([])
    const supplierOptions = ref([])
    const supplierFilter = (input, option) => (option.value || '').includes(input)
    const formSupplierFilter = ref('')

    const searchForm = reactive({ materialId: undefined, type: undefined, dateRange: [] })
    const pagination = reactive({
      current: 1, pageSize: 20, total: 0,
      showSizeChanger: true,
      showTotal: (total) => `共 ${total} 条`
    })

    const editVisible = ref(false)
    const form = reactive({
      id: null, materialId: undefined,
      usageDate: null, quantity: 0, purpose: '', remark: ''
    })

    const columns = [
      { title: '使用日期', key: 'usageDate', width: 110, fixed: 'left' },
      { title: '类型', key: 'type', width: 70 },
      { title: '原料', dataIndex: 'materialName', key: 'materialName', width: 160, ellipsis: true },
      { title: '数量', key: 'quantity', width: 120, align: 'right' },
      { title: '用途', dataIndex: 'purpose', key: 'purpose', ellipsis: true },
      { title: '备注', dataIndex: 'remark', key: 'remark', ellipsis: true, width: 200 },
      { title: '操作', key: 'action', width: 130, fixed: 'right' }
    ]

    const typeLabel = (t) => ({ FABRIC: '面料', ACCESSORY: '辅料', CONSUMABLE: '耗材' })[t] || t
    const typeColor = (t) => ({ FABRIC: 'cyan', ACCESSORY: 'purple', CONSUMABLE: 'orange' })[t] || 'default'

    const filterOption = (input, option) => (option.label || '').toLowerCase().includes(input.toLowerCase())
    const getScrollContainer = () => document.querySelector('.content') || window

    const currentMaterial = computed(() => materials.value.find(m => m.id === form.materialId))
    const unit = computed(() => unitText(currentMaterial.value?.unit) || '')

    const formTypeFilter = ref('')

    const matchesSupplier = (m) => {
      const s = (formSupplierFilter.value || '').trim()
      if (!s || !m || !m.suppliers) return false
      return m.suppliers.split(',').map(x => x.trim()).filter(Boolean).includes(s)
    }

    const filteredFormMaterials = computed(() => {
      const base = formTypeFilter.value
        ? materials.value.filter(m => m.type === formTypeFilter.value)
        : materials.value.slice()
      const s = (formSupplierFilter.value || '').trim()
      if (!s) return base
      const matched = []
      const rest = []
      base.forEach(m => (matchesSupplier(m) ? matched : rest).push(m))
      return [...matched, ...rest]
    })

    /** Available stock for the chosen material, excluding this record if editing */
    const availableStock = computed(() => {
      if (!currentMaterial.value) return 0
      const cur = Number(currentMaterial.value.stock || 0)
      // If editing the same material, add back the existing quantity
      if (form.id) {
        const original = records.value.find(r => r.id === form.id)
        if (original && original.materialId === form.materialId) {
          return cur + Number(original.quantity || 0)
        }
      }
      return cur
    })
    const exceedsStock = computed(() => {
      const qty = Number(form.quantity || 0)
      return currentMaterial.value && qty > availableStock.value
    })
    const stockClass = computed(() => {
      if (!currentMaterial.value) return ''
      const s = Number(currentMaterial.value.stock || 0)
      if (s <= 0) return 'stock-empty'
      const p = Number(currentMaterial.value.totalPurchased || 0)
      if (p > 0 && s / p < 0.2) return 'stock-low'
      return 'stock-ok'
    })

    const formatDate = (s) => !s || s.length !== 8 ? s || '' : `${s.substring(0,4)}-${s.substring(4,6)}-${s.substring(6,8)}`
    const formatNum = (n) => Number(n || 0).toFixed(2).replace(/\.00$/, '')
    const unitText = (u) => {
      if (!u) return ''
      const legacy = { METER: '米', YARD: '码', PIECE: '个', PAIR: '对', PACK: '包' }
      return legacy[u] || u
    }

    const buildParams = () => {
      const params = {
        materialId: searchForm.materialId || undefined,
        type: searchForm.type || undefined,
        pageNo: pagination.current,
        pageSize: pagination.pageSize
      }
      if (searchForm.dateRange?.length === 2) {
        params.startDate = searchForm.dateRange[0].format('YYYYMMDD')
        params.endDate = searchForm.dateRange[1].format('YYYYMMDD')
      }
      return params
    }

    const loadList = async () => {
      loading.value = true
      try {
        const result = await materialApi.listUsage(buildParams())
        if (result.code === '0') {
          records.value = result.data.records || []
          pagination.total = result.data.total || 0
        }
      } finally {
        loading.value = false
      }
    }

    const loadMaterials = async () => {
      try {
        // Use listMaterial to get stock/totalPurchased/totalUsed for each material
        const result = await materialApi.listMaterial({ status: 1 })
        if (result.code === '0') materials.value = result.data || []
      } catch (e) { console.error(e) }
    }

    const loadSuppliers = async () => {
      try {
        const result = await materialApi.listSuppliers()
        if (result.code === '0') {
          supplierOptions.value = (result.data || []).map(s => ({ value: s }))
        }
      } catch (e) { console.error(e) }
    }

    const handleTableChange = (pag) => {
      pagination.current = pag.current
      pagination.pageSize = pag.pageSize
      loadList()
    }
    const handleSearch = () => { pagination.current = 1; loadList() }
    const handleReset = () => {
      Object.assign(searchForm, { materialId: undefined, type: undefined, dateRange: [] })
      pagination.current = 1
      loadList()
    }

    const openEdit = (record) => {
      formTypeFilter.value = ''
      formSupplierFilter.value = ''
      if (record) {
        form.id = record.id
        form.materialId = record.materialId
        form.usageDate = record.usageDate ? dayjs(record.usageDate, 'YYYYMMDD') : null
        form.quantity = Number(record.quantity || 0)
        form.purpose = record.purpose || ''
        form.remark = record.remark || ''
      } else {
        Object.assign(form, {
          id: null, materialId: undefined, usageDate: dayjs(),
          quantity: 0, purpose: '', remark: ''
        })
      }
      editVisible.value = true
    }

    const handleSubmit = async () => {
      if (!form.materialId) { message.error('请选择原料'); return }
      if (!form.quantity || form.quantity <= 0) { message.error('数量必须大于 0'); return }
      submitting.value = true
      try {
        const result = await materialApi.saveUsage({
          id: form.id, materialId: form.materialId,
          usageDate: form.usageDate ? form.usageDate.format('YYYYMMDD') : null,
          quantity: form.quantity, purpose: form.purpose, remark: form.remark
        })
        if (result.code === '0') {
          message.success('保存成功')
          editVisible.value = false
          loadList()
          loadMaterials()
        } else {
          message.error(result.msg || '保存失败')
        }
      } finally {
        submitting.value = false
      }
    }

    const handleDelete = async (record) => {
      try {
        const result = await materialApi.deleteUsage(record.id)
        if (result.code === '0') { message.success('删除成功'); loadList(); loadMaterials() }
        else message.error(result.msg || '删除失败')
      } catch (e) { console.error(e) }
    }

    const handleExport = async () => {
      exportLoading.value = true
      try {
        const params = buildParams()
        delete params.pageNo; delete params.pageSize
        const response = await materialApi.exportUsage(params)
        const blob = new Blob([response.data], { type: 'application/vnd.ms-excel' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        const now = new Date()
        const ts = now.getFullYear() + String(now.getMonth() + 1).padStart(2, '0') + String(now.getDate()).padStart(2, '0') +
          '_' + String(now.getHours()).padStart(2, '0') + String(now.getMinutes()).padStart(2, '0') + String(now.getSeconds()).padStart(2, '0')
        link.download = `原料使用_${ts}.xlsx`
        document.body.appendChild(link); link.click(); document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        message.success('导出成功')
      } catch (e) {
        console.error(e); message.error('导出失败')
      } finally {
        exportLoading.value = false
      }
    }

    onMounted(() => { loadMaterials(); loadSuppliers(); loadList() })

    return {
      loading, submitting, exportLoading,
      records, materials, supplierOptions, supplierFilter, formSupplierFilter,
      searchForm, pagination, columns,
      editVisible, form, unit,
      formTypeFilter, filteredFormMaterials, matchesSupplier,
      currentMaterial, availableStock, exceedsStock, stockClass,
      filterOption, getScrollContainer,
      formatDate, formatNum, unitText, typeLabel, typeColor,
      handleTableChange, handleSearch, handleReset,
      openEdit, handleSubmit, handleDelete, handleExport
    }
  }
}
</script>

<style scoped>
.usage-list { padding: 0; }
.page-header {
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;
}
.page-header h2 { margin: 0; }
.filter-card { margin-bottom: 16px; }

.stock-info {
  background: #f6f9ff;
  border: 1px solid #e6f0ff;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.stock-info-label {
  color: #666;
  font-size: 13px;
}
.stock-info-value {
  font-size: 18px;
  font-weight: 600;
}
.stock-info-value.stock-ok { color: #52c41a; }
.stock-info-value.stock-low { color: #fa8c16; }
.stock-info-value.stock-empty { color: #999; }
.stock-info-detail {
  color: #999;
  font-size: 12px;
}
.stock-info-warn {
  color: #ff4d4f;
  font-size: 13px;
  font-weight: 600;
  margin-left: auto;
}
</style>
